import React from 'react';
import CircularPackageSlider from '../components/CircularPackageSlider';
import { internationalPackages } from '../mock';
import { Globe, Shield, Headphones, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const InternationalPackages = () => {
  return (
    <div className="min-h-screen pt-32 pb-20">
      {/* Hero Section */}
      <section className="relative h-96 mb-20 overflow-hidden">
        <img 
          src="https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1200" 
          alt="International Travel"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-red-900/80"></div>
        <div className="relative z-10 container mx-auto px-4 h-full flex items-center justify-center text-center text-white">
          <div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Explore the World
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto">
              Discover breathtaking destinations across the globe with our curated international packages
            </p>
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="container mx-auto px-4 mb-20">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            International Destinations
          </h2>
          <p className="text-xl text-gray-600">
            From tropical paradises to vibrant cities - explore them all
          </p>
        </div>

        <CircularPackageSlider packages={internationalPackages} />
      </section>

      {/* Features Section */}
      <section className="container mx-auto px-4 mb-20">
        <div className="bg-gradient-to-br from-blue-50 to-red-50 rounded-3xl p-8 md:p-12">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            Why Book International with Us?
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-2xl p-6 text-center shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe size={32} className="text-blue-600" />
              </div>
              <h3 className="font-bold text-lg mb-2">Visa Assistance</h3>
              <p className="text-gray-600 text-sm">Complete visa support and documentation help</p>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105">
              <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield size={32} className="text-green-600" />
              </div>
              <h3 className="font-bold text-lg mb-2">Travel Insurance</h3>
              <p className="text-gray-600 text-sm">Comprehensive travel insurance included</p>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105">
              <div className="w-16 h-16 bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <Headphones size={32} className="text-red-600" />
              </div>
              <h3 className="font-bold text-lg mb-2">24/7 Support</h3>
              <p className="text-gray-600 text-sm">Round-the-clock assistance during your trip</p>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-purple-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <CreditCard size={32} className="text-purple-600" />
              </div>
              <h3 className="font-bold text-lg mb-2">Easy Booking</h3>
              <p className="text-gray-600 text-sm">Simple payment plans and flexible options</p>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Destinations */}
      <section className="container mx-auto px-4 mb-20">
        <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
          Popular International Destinations
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {['Bali', 'Dubai', 'Thailand', 'Singapore', 'Maldives', 'Sri Lanka'].map((destination, index) => (
            <div 
              key={index}
              className="bg-white rounded-xl p-4 text-center shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer"
            >
              <div className="text-3xl mb-2">
                {index === 0 ? '🏝️' : index === 1 ? '🏛️' : index === 2 ? '🌴' : index === 3 ? '🏙️' : index === 4 ? '🏖️' : '🌿'}
              </div>
              <div className="font-semibold text-gray-900">{destination}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4">
        <div className="bg-gradient-to-r from-blue-600 to-red-600 rounded-3xl p-8 md:p-12 text-white text-center">
          <h2 className="text-4xl font-bold mb-6">Ready for Your International Adventure?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Let us help you plan the perfect international getaway. Contact us today!
          </p>
          <Button 
            asChild
            size="lg"
            className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-6 transition-all duration-300 hover:scale-105"
          >
            <Link to="/contact">Get in Touch</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default InternationalPackages;